import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import {
	TextField,
	MenuItem,
	FormControl,
	Typography,
	Grid,
	Button,
	IconButton,
	Box,
	RadioGroup,
	Radio,
	FormControlLabel,
	InputAdornment
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import {
	Add,
	FormatAlignLeft,
	FormatAlignRight,
	FormatBold,
	FormatItalic,
	FormatUnderlined,
	StrikethroughS
} from '@material-ui/icons'
import { GET_ME_ANALYTICS_USER } from 'constants/queries/analyticsUser'
import { useQuery } from '@apollo/client'
import SectionSelect from './Sections'
import SubSectionSelect from './Subsections'

import useIntl from 'hooks/useIntl'
import iziToast from 'izitoast'
import { useStoreon } from 'storeon/react'

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api/v1/custom`
const EDITOR_MIN_HEIGHT = '80px'
const ANSWERS_MIN_HEIGHT = 120

const QUESTION_TYPES = [
	'Multiple Choice (Radiobutton)',
	'Multiple Choice (Dropdown)',
	'Fill in the Blank',
	'True/False',
	'Multiple Correct',
	'Essay (Evaluated by Admin)',
	'Drag and Match',
	'Matching',
	'All Correct',
	'Audio Answer'
]

const useStyles = makeStyles(theme => ({
	inputField: {
		borderRadius: theme.shape.borderRadius,
		'& fieldset': { border: 'none' }
	},
	formControl: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		width: '100%'
	},
	iconButton: {
		backgroundColor:
			theme.palette.mode === 'dark'
				? theme.palette.background.paper
				: theme.palette.grey[200],

		marginLeft: theme.spacing(0.5)
		// padding: theme.spacing(0.5, 1)
	},
	editor: {
		width: '100%',
		height: EDITOR_MIN_HEIGHT,
		// padding: theme.spacing(1),
		border: `1px solid rgb(228, 231, 237)`,

		outline: 'none',
		overflowY: 'scroll',
		wordBreak: 'break-word',
		whiteSpace: 'pre-wrap',
		overflowWrap: 'break-word',
		scrollbarWidth: 'none',
		'&::-webkit-scrollbar': {
			display: 'none'
		}
	},
	menuPaper: {
		maxHeight: 200,
		overflowY: 'auto'
	},
	formGrid: {
		'@media (max-width:500px)': {
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			width: '100%'
		}
	},
	formattingToolbar: {
		display: 'flex',
		gap: theme.spacing(1),
		padding: theme.spacing(1),

		border: `1px solid rgb(228, 231, 237)`
		// marginBottom: theme.spacing(1)
	}
}))

interface SubSection {
	subSectionId: number
	subSectionName: string
}

interface Section {
	sectionId: number
	sectionName: string
	subSections: SubSection[]
}

const toUnicodeBold = (text: string) =>
	text.replace(/[A-Za-z]/g, c =>
		String.fromCodePoint(
			c.charCodeAt(0) + (c >= 'A' && c <= 'Z' ? 0x1d5d4 - 65 : 0x1d5ee - 97)
		)
	)

const toUnicodeItalic = (text: string) =>
	text.replace(/[A-Za-z]/g, c =>
		String.fromCodePoint(
			c.charCodeAt(0) + (c >= 'A' && c <= 'Z' ? 0x1d608 - 65 : 0x1d622 - 97)
		)
	)

const toUnicodeUnderline = (text: string) =>
	text
		.split('')
		.map(c => c + '\u0332')
		.join('')

const toUnicodeStrikethrough = (text: string) =>
	text
		.split('')
		.map(char => char + '\u0336')
		.join('')

export default function Selection({ handleClose, refreshData }) {
	const theme = useTheme()
	const classes = useStyles()

	const [questionName, setQuestionName] = useState('')
	// const [questionType, setQuestionType] = useState('')
	const [questionType, setQuestionType] = useState('Multiple Choice (Radiobutton)')
	const [options, setOptions] = useState(['', '', '', '']) // default two options
	const [selectedOption, setSelectedOption] = useState('')

	const [question, setQuestion] = useState('')
	const [answers, setAnswers] = useState('')
	const [correctAnswer, setCorrectAnswer] = useState('')
	const [explanation, setExplanation] = useState('')
	const [marks, setMarks] = useState('')
	const [selectedSection, setSelectedSection] = useState('')

	const [sectionData, setSectionData] = useState<Section[]>([])
	const [subSectionData, setSubSectionData] = useState<SubSection[]>([])
	const [errors, setErrors] = useState<{ [key: string]: string }>({})
	const [isCustomSection, setIsCustomSection] = useState(false)
	const [customSection, setCustomSection] = useState('')
	const [isCustomSubSection, setIsCustomSubSection] = useState(false)
	const [customSubSection, setCustomSubSection] = useState('')
	const [selectedOptions, setSelectedOptions] = useState<string[]>([])
	const [selectedSubSection, setSelectedSubSection] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const intl = useIntl()
	const [textAlign, setTextAlign] = useState<'left' | 'right'>('left')

	const editorRef = useRef<HTMLDivElement | null>(null)

	const { data } = useQuery(GET_ME_ANALYTICS_USER)
	const { token } = useStoreon('token')
	useEffect(() => {
		if (data && data.me) {
			localStorage.setItem('userId', data.me.id)
		}
	}, [data])

	const fetchSections = async () => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/getSections`,
				{
					headers: {
						Authorization: ` ${token}`
					}
				}
			)

			const sections = response.data.map((section: any) => ({
				sectionId: section._id,
				sectionName: section.name,
				subSections: []
			}))
			setSectionData(sections)
		} catch (error) {
			console.error('Error fetching sections:', error)
		}
	}

	useEffect(() => {
		fetchSections()
	}, [])

	const fetchSubSectionsWithSectionId = async (sectionId: string) => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/getSubSectionsWithSectionId/${sectionId}`,
				{
					headers: {
						Authorization: ` ${token}`
					}
				}
			)

			const formattedSubSections = response.data.map(item => ({
				subSectionId: item._id,
				subSectionName: item.name,
				sectionName: item.sectionName
			}))

			setSubSectionData(formattedSubSections)
		} catch (error) {
			console.error('Error fetching subsections:', error)
		}
	}
	useEffect(() => {
		if (selectedSection) {
			fetchSubSectionsWithSectionId(selectedSection)
		}
	}, [selectedSection])

	const handleSubmit = async (event: React.FormEvent) => {
		setIsLoading(true)
		event.preventDefault()

		const userId = localStorage.getItem('userId')

		const selectedSectionObj = sectionData.find(
			section => section.sectionId.toString() === selectedSection
		)
		const selectedSubSectionObj = subSectionData.find(
			subSection => subSection.subSectionId.toString() === selectedSubSection
		)

		let formattedAnswers: string[] = []
		let formattedCorrectAnswer: string[] = []

		if (
			questionType === 'Multiple Choice (Radiobutton)' ||
			questionType === 'Multiple Choice (Dropdown)'
		) {
			formattedAnswers = options.filter(option => option.trim() !== '')
			formattedCorrectAnswer = correctAnswer ? [correctAnswer.trim()] : []
		} else if (questionType === 'Fill in the Blank') {
			formattedAnswers = [answers.trim()]
			formattedCorrectAnswer = correctAnswer ? [correctAnswer.trim()] : []
		} else if (questionType === 'True/False') {
			formattedAnswers = ['True', 'False']
			formattedCorrectAnswer = correctAnswer ? [correctAnswer.trim()] : []
		}

		const payload: any = {
			questionName,
			questionType,
			question,
			answers: formattedAnswers,
			correctAnswer: formattedCorrectAnswer,
			explanation,
			marks,
			sectionId: selectedSectionObj?.sectionId || '',
			sectionName: customSection || selectedSectionObj?.sectionName || '',
			subSectionId: selectedSubSectionObj?.subSectionId || '',

			subSectionName: customSubSection || selectedSubSectionObj?.subSectionName || '',
			user_id: userId || ''
		}

		// console.log('Submitting Data:', payload)

		try {
			await axios.post(`${API_BASE_URL}/createQuestion`, payload, {
				headers: {
					Authorization: ` ${token}`
				}
			})

			iziToast.success({
				message: 'Question added successfully'
			})

			// console.log('Question added successfully!')
			handleClose()
			refreshData()
			resetForm()
		} catch (error: any) {
			console.error('API Error:', error.response ? error.response.data : error.message)

			iziToast.error({
				message: error.response?.data?.message
			})
		}
	}

	const resetForm = () => {
		setQuestionName('')
		setQuestionType('')
		setQuestion('')
		setAnswers('')
		setCorrectAnswer('')
		setExplanation('')
		setMarks('')
		setSelectedSection('')
		setSelectedSubSection('')
		setCustomSection('')
		setCustomSubSection('')
		setSelectedOptions([])
		setSelectedOption('')
		setOptions([''])

		if (editorRef.current) {
			editorRef.current.innerHTML = ''
		}
	}

	const handleCancel = () => {
		resetForm()
		handleClose()
	}

	const handleQuestionTypeChange = e => {
		const selectedType = e.target.value
		setQuestionType(selectedType)
		setSelectedOption('')
		setAnswers('')

		if (selectedType === 'True/False') {
			setOptions(['True', 'False'])
		} else if (selectedType === 'Multiple Choice (Radiobutton)') {
			setOptions(['', '', '', ''])
		} else if (selectedType === 'Multiple Choice (Dropdown)') {
			setOptions(['', '', '', ''])
		} else if (selectedType === 'Fill in the Blank') {
			setAnswers('')
		}
	}

	const handleOptionChange = (index, value) => {
		const newOptions = [...options]
		newOptions[index] = value
		setOptions(newOptions)
	}

	const applyFormat = (
		formatType: 'bold' | 'italic' | 'underline' | 'fx' | 'left' | 'right'
	) => {
		if (editorRef.current) {
			let text = editorRef.current.innerText.trim()

			if (formatType === 'bold') text = toUnicodeBold(text)
			if (formatType === 'italic') text = toUnicodeItalic(text)
			if (formatType === 'underline') text = toUnicodeUnderline(text)
			if (formatType === 'fx') text = toUnicodeStrikethrough(text)

			if (formatType === 'left') {
				setTextAlign('left')
			}

			if (formatType === 'right') {
				setTextAlign('right')
			}

			setQuestion(text)
			editorRef.current.innerText = text
			editorRef.current.focus()
		}
	}

	const addStartLine = () => {
		if (editorRef.current) {
			editorRef.current.innerText = '\n' + editorRef.current.innerText
			setQuestion(editorRef.current.innerText.trim())
			editorRef.current.focus()
		}
	}

	const menuProps = {
		getContentAnchorEl: null,
		anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
		transformOrigin: { vertical: 'top', horizontal: 'left' },
		PaperProps: { className: classes.menuPaper }
	}
	const formattedSectionData = sectionData.map(section => ({
		id: section.sectionId,
		name: section.sectionName
	}))
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			<Grid
				container
				spacing={2}
				display="flex"
				alignItems="center"
				justifyContent="center"
				className={classes.formGrid}
			>
				<Grid item xs={12}>
					<FormControl className={classes.formControl}>
						<Typography gutterBottom>
							{intl.formatMessage({ id: 'Question Type' })}
						</Typography>
						<TextField
							select
							fullWidth
							size="small"
							value={questionType}
							onChange={handleQuestionTypeChange}
							InputProps={{ className: classes.inputField }}
							SelectProps={{
								MenuProps: {
									getContentAnchorEl: null,
									anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
									transformOrigin: { vertical: 'top', horizontal: 'left' },
									PaperProps: {
										style: { maxHeight: 200, overflowY: 'auto' }
									}
								}
							}}
						>
							{QUESTION_TYPES.map(type => (
								<MenuItem key={type} value={type}>
									{type}
								</MenuItem>
							))}
						</TextField>
					</FormControl>
				</Grid>

				<Grid item xs={12}>
					<Typography gutterBottom>
						{intl.formatMessage({ id: 'Question Name' })}
					</Typography>
					<TextField
						fullWidth
						placeholder="Enter question name"
						margin="normal"
						value={questionName}
						onChange={e => setQuestionName(e.target.value)}
						error={!!errors.questionName}
						helperText={errors.questionName}
						InputProps={{
							className: classes.inputField
						}}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<Box>
						<SectionSelect
							sectionData={sectionData}
							selectedSection={selectedSection}
							setSelectedSection={setSelectedSection}
							setSubSectionData={setSubSectionData}
							isCustomSection={isCustomSection}
							setIsCustomSection={setIsCustomSection}
							customSection={customSection}
							setCustomSection={setCustomSection}
							fetchSections={fetchSections}
						/>
					</Box>
				</Grid>

				<Grid item xs={12} sm={6}>
					<Box>
						<SubSectionSelect
							sectionData={formattedSectionData}
							subSectionData={subSectionData}
							setSubSectionData={setSubSectionData}
							selectedSubSection={selectedSubSection}
							setSelectedSubSection={setSelectedSubSection}
							isCustomSubSection={isCustomSubSection}
							setIsCustomSubSection={setIsCustomSubSection}
							customSubSection={customSubSection}
							setCustomSubSection={setCustomSubSection}
							selectedSection={selectedSection}
							refetchSubSections={fetchSubSectionsWithSectionId}
						/>
					</Box>
				</Grid>

				<Grid item xs={12}>
					<Typography gutterBottom>{intl.formatMessage({ id: 'Question' })}</Typography>

					<Box className={classes.formattingToolbar} sx={{ border: 'none' }}>
						<IconButton size="small" onClick={() => applyFormat('bold')}>
							<FormatBold />
						</IconButton>
						<IconButton size="small" onClick={() => applyFormat('italic')}>
							<FormatItalic />
						</IconButton>
						<IconButton size="small" onClick={() => applyFormat('underline')}>
							<FormatUnderlined />
						</IconButton>
						<IconButton size="small" onClick={() => applyFormat('fx')}>
							<StrikethroughS />
						</IconButton>
						<IconButton size="small" onClick={addStartLine}>
							<Add />
						</IconButton>
						<IconButton size="small" onClick={() => applyFormat('left')}>
							<FormatAlignLeft />
						</IconButton>
						<IconButton size="small" onClick={() => applyFormat('right')}>
							<FormatAlignRight />
						</IconButton>
					</Box>

					<Box
						contentEditable
						ref={editorRef}
						suppressContentEditableWarning
						onInput={() => setQuestion(editorRef.current?.innerText.trim() || '')}
						className={classes.editor}
					/>
				</Grid>

				<Grid item xs={12}>
					<Typography gutterBottom>{intl.formatMessage({ id: 'Answers' })}</Typography>
					<Box minHeight={ANSWERS_MIN_HEIGHT} sx={{ width: '100%' }}>
						{questionType === 'Fill in the Blank' ? (
							<TextField
								fullWidth
								multiline
								rows={4}
								value={answers}
								onChange={e => setAnswers(e.target.value)}
								placeholder="Enter correct answers separated by commas"
								error={!!errors.answers}
								helperText={errors.answers}
								InputProps={{
									className: classes.inputField
								}}
							/>
						) : (
							<FormControl component="fieldset" fullWidth>
								<RadioGroup
									value={selectedOption}
									onChange={e => setSelectedOption(e.target.value)}
								>
									<Grid container spacing={1}>
										{(questionType === 'Multiple Choice (Radiobutton)' ||
											questionType === 'Multiple Choice (Dropdown)') &&
											options.map((option, index) => (
												<Grid item xs={12} key={index}>
													<TextField
														fullWidth
														size="small"
														value={option}
														onChange={e => handleOptionChange(index, e.target.value)}
														placeholder={`Answer Option ${index + 1}`}
														className={classes.inputField}
													/>
												</Grid>
											))}
										{questionType === 'True/False' &&
											['True', 'False'].map((label, index) => (
												<Grid item xs={12} key={index}>
													<FormControlLabel
														control={
															<Radio
																value={label}
																checked={selectedOption === label}
																onChange={() => setSelectedOption(label)}
																sx={{
																	color: 'lightgray',
																	'&.Mui-checked': { color: 'primary.main' }
																}}
															/>
														}
														label={label}
													/>
												</Grid>
											))}
									</Grid>
								</RadioGroup>
							</FormControl>
						)}
					</Box>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Typography gutterBottom>
						{intl.formatMessage({ id: 'Correct Answers' })}
					</Typography>
					<TextField
						placeholder="Enter correct answer"
						fullWidth
						margin="normal"
						value={correctAnswer}
						onChange={e => setCorrectAnswer(e.target.value)}
						error={!!errors.correctAnswer}
						helperText={errors.correctAnswer}
						InputProps={{
							className: classes.inputField
						}}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<Typography gutterBottom>{intl.formatMessage({ id: 'Marks' })}</Typography>
					<TextField
						fullWidth
						placeholder="Enter marks"
						type="number"
						value={marks}
						onChange={e => setMarks(e.target.value)}
						error={!!errors.marks}
						helperText={errors.marks}
						InputProps={{
							className: classes.inputField
						}}
					/>
				</Grid>

				<Grid item xs={12}>
					<Typography gutterBottom>{intl.formatMessage({ id: 'Explanation' })}</Typography>
					<TextField
						multiline
						placeholder="Enter explanation"
						rows={4}
						fullWidth
						margin="normal"
						value={explanation}
						onChange={e => setExplanation(e.target.value)}
						error={!!errors.explanation}
						helperText={errors.explanation}
						InputProps={{
							className: classes.inputField
						}}
					/>
				</Grid>

				<Grid item xs={12} sm={6}></Grid>

				<Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
					<Grid item>
						<Button variant="outlined" onClick={handleCancel}>
							{intl.formatMessage({ id: 'Cancel' })}
						</Button>
					</Grid>
					<Grid item>
						<Button
							variant="contained"
							color="primary"
							onClick={handleSubmit}
							disabled={isLoading}
						>
							{/* {intl.formatMessage({ id: 'Add' })} */}
							{isLoading ? 'Creating...' : 'Create'}
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	)
}
